/**
 * React Query wrapper around `fetchSuggestedInterviewer`.
 * Fetches the AI-recommended interviewer for a given job-stage action.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchSuggestedInterviewer,
  type FetchSuggestedInterviewerArgs,
  type SuggestedInterviewer,
} from './fetchSuggestedInterviewer';

export interface UseSuggestedInterviewerArgs extends Omit<
  FetchSuggestedInterviewerArgs,
  'signal'
> {
  enabled?: boolean;
}

export function useSuggestedInterviewer(
  args: UseSuggestedInterviewerArgs
): UseQueryResult<SuggestedInterviewer | null, Error> {
  const { enabled = true, atsJobStageActionId, ...rest } = args;
  return useQuery<SuggestedInterviewer | null, Error>({
    queryKey: [
      'ats',
      'suggested_interviewer',
      atsJobStageActionId,
      rest.startAt,
      rest.endAt,
    ],
    queryFn: ({ signal }) =>
      fetchSuggestedInterviewer({ atsJobStageActionId, ...rest, signal }),
    // Backend requires a parseable start_at/end_at with start < end; skip
    // the call if either is missing so we don't spam 400s.
    enabled:
      enabled &&
      atsJobStageActionId > 0 &&
      typeof rest.startAt === 'string' &&
      rest.startAt.length > 0 &&
      typeof rest.endAt === 'string' &&
      rest.endAt.length > 0,
    staleTime: 60_000,
    retry: false,
  });
}
